import React, { useState, useEffect } from "react";
import { TextField, InputAdornment, Button } from "@mui/material";
import { MdSearch, MdRemoveRedEye, MdDelete, MdClose } from "react-icons/md";
import {
  getImportedData,
  getImportedFileData,
  importMT202,
} from "../../../Api";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import dayjs from "dayjs";

function MT202Tab() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [content, setContent] = useState({
    file_type: "MT202",
    file: null,
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileDetails, setSelectedFileDetails] = useState(null);
  const [selectedFileAllDetails, setSelectedFileAllDetails] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [modalPageSize] = useState(5);
  const [modalTotalCount, setModalTotalCount] = useState(0);

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      setContent((prev) => ({
        ...prev,
        file: e.target.files[0],
      }));
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const fetchImportedFiles = async () => {
    setLoading(true);
    try {
      const response = await getImportedFileData({
        file_type: "MT202",
        bank_code: localStorage.getItem("bank_code") || "",
        page: currentPage,
        pageSize: pageSize,
      });
      if (response?.status === "S") {
        setUploadedFiles(response.result_info || []);
        setTotalCount(response.pagination?.totalCount || 0);
      } else {
        setUploadedFiles([]);
        setTotalCount(0);
      }
    } catch (error) {
      setUploadedFiles([]);
      setTotalCount(0);
      console.error("Error fetching data:", error);
      Swal.fire({
        text: "Error fetching files",
        icon: "error",
        showConfirmButton: true,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchImportedFiles();
  }, [currentPage]); // Re-fetch when page changes

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.file) {
      Swal.fire({
        text: "Please upload a file!",
        icon: "warning",
        showConfirmButton: false,
        heightAuto: false,
        timer: 1500,
      });
      return;
    }
    try {
      const formData = new FormData();
      formData.append("file_type", content.file_type);
      formData.append("file", content.file);
      formData.append("bank_code", localStorage.getItem("bank_code") || "");
      formData.append("correl_id", localStorage.getItem("correl_id") || "");
      Swal.fire({
        html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });
      const response = await importMT202(formData);
      Swal.close();
      if (response?.status === "S") {
        Swal.fire({
          text: "File uploaded successfully!",
          icon: "success",
          showConfirmButton: true,
          heightAuto: false,
        });
        setContent({
          file_type: "MT202",
          file: null,
        });
        setImportModal(false);
        setCurrentPage(1); // Reset to first page after upload
        fetchImportedFiles();
      } else {
        Swal.fire({
          text: response?.message || "Error uploading file",
          icon: "error",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      Swal.fire({
        text: "Error uploading file",
        icon: "error",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const handleOpenViewModal = async (file) => {
    setSelectedFile({
      file_name: file.file_name,
      uploadedAt: file.load_date ? `${file.load_date} ${file.load_time || ""}` : "",
      bankCode: file.bank_code,
    });
    setViewModal(true);
    setSelectedFileDetails(null);
    setModalCurrentPage(1);
    
    try {
      const detailsResponse = await getImportedData({
        file_type: "MT202",
        file_name: file.file_name,
        bank_code: file.bank_code,
        page: 1,
        pageSize: 10000,
      });

      if (detailsResponse?.status === "S") {
        const allDetails = detailsResponse.result_info || [];
        setSelectedFileAllDetails(allDetails);
        setModalTotalCount(allDetails.length);
        setSelectedFileDetails(allDetails.slice(0, modalPageSize));
        setModalCurrentPage(1);
      } else {
        setSelectedFileAllDetails([]);
        setSelectedFileDetails([]);
        setModalTotalCount(0);
        Swal.fire({
          text: detailsResponse?.message || "Error fetching file details",
          icon: "error",
          showConfirmButton: true,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({
        text: "Error fetching file details",
        icon: "error",
        showConfirmButton: true,
      });
      setSelectedFileAllDetails([]);
      setSelectedFileDetails([]);
      setModalTotalCount(0);
    }
  };

  const fetchModalData = (pageNumber) => {
    if (!selectedFileAllDetails || selectedFileAllDetails.length === 0) return;
    const startIndex = (pageNumber - 1) * modalPageSize;
    const endIndex = startIndex + modalPageSize;
    setSelectedFileDetails(selectedFileAllDetails.slice(startIndex, endIndex));
    setModalCurrentPage(pageNumber);
  };

  const handleModalFirstPage = () => {
    if (modalCurrentPage !== 1) {
      fetchModalData(1);
    }
  };

  const handleModalPrevPage = () => {
    if (modalCurrentPage > 1) {
      fetchModalData(modalCurrentPage - 1);
    }
  };

  const handleModalNextPage = () => {
    const totalPages = Math.ceil(modalTotalCount / modalPageSize);
    if (modalCurrentPage < totalPages) {
      fetchModalData(modalCurrentPage + 1);
    }
  };

  const handleModalLastPage = () => {
    const totalPages = Math.ceil(modalTotalCount / modalPageSize);
    if (modalCurrentPage !== totalPages) {
      fetchModalData(totalPages);
    }
  };
  // const handleDelete = async (file) => {
  //   try {
  //     // Show confirmation dialog first
  //     const result = await Swal.fire({
  //       title: 'Are you sure?',
  //       text: "You want to delete this file?",
  //       icon: 'warning',
  //       showCancelButton: true,
  //       confirmButtonColor: '#3085d6',
  //       cancelButtonColor: '#d33',
  //       confirmButtonText: 'Yes, delete it!'
  //     });

  //     if (result.isConfirmed) {
  //       // Show loading
  //       Swal.fire({
  //         html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
  //         showConfirmButton: false,
  //         allowOutsideClick: false
  //       });

  //       const response = await deleteProcessedData({
  //         file_type: "MT202",
  //         file_name: file.file_name,
  //         bank_code: file.bank_code,
  //         correl_id: localStorage.getItem("correl_id") || "",
  //         scenario: file.scenario || [],
  //         processed_id: file.processed_id
  //       });

  //       Swal.close();

  //       if (response?.status === "S") {
  //         Swal.fire({
  //           text: response.message || "File deleted successfully!",
  //           icon: "success",
  //           showConfirmButton: true
  //         });
          
  //         // Refresh the file list after successful deletion
  //         fetchImportedFiles();
  //       } else {
  //         Swal.fire({
  //           text: response?.message || "Error deleting file",
  //           icon: "error",
  //           showConfirmButton: true
  //         });
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Delete error:", error);
  //     Swal.fire({
  //       text: "Error deleting file",
  //       icon: "error",
  //       showConfirmButton: true
  //     });
  //   }
  // };

  const filteredFiles = uploadedFiles.filter(
    (file) =>
      file.file_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (file.load_date && file.load_date.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (file.load_time && file.load_time.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const renderFileList = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan="4" className="text-center">
            <img src={require("../../../assets/loader.gif")} alt="loader" style={{ width: "50px" }} />
          </td>
        </tr>
      );
    }

    if (!filteredFiles.length) {
      return (
        <tr>
          <td colSpan="4" className="text-center">
            No files found
          </td>
        </tr>
      );
    }
    return filteredFiles.map((file, index) => (
      <tr key={file.file_name}>
        <td>{(currentPage - 1) * pageSize + index + 1}</td>
        <td>{file.file_name || "--"}</td>
        <td>{file.load_date ? `${dayjs(file.load_date).format('DD-MM-YYYY')}, ${file.load_time || ''}` : "--"}</td>
        <td className="text-center">
          <button className="btn btn-sm btn-primary me-2" onClick={() => handleOpenViewModal(file)}>
            <MdRemoveRedEye /> View
          </button>
          <button className="btn btn-sm btn-danger">
            <MdDelete /> Delete
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center my-3">
        <div>
          <TextField
            size="small"
            variant="standard"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInput-underline:before": {
                borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "2px solid var(--theme-color)",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottom: "2px solid rgba(0, 0, 0, 0.87)",
              },
            }}
          />
        </div>
        <button className="btn createButton mx-2" onClick={() => setImportModal(true)}>
          Import
        </button>
      </div>

      <div className="table-responsive mb-3">
        <table className="table table-bordered table-striped">
          <thead className="table-dark">
            <tr>
              <th>#</th>
              <th>File Name</th>
              <th>Uploaded At</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>{renderFileList()}</tbody>
        </table>
      </div>

      {importModal && (
        <div className="modal-overlay d-flex flex-column">
          <div className="modal-container">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Import MT202 File</h5>
              <button type="button" className="btn-close" onClick={() => setImportModal(false)}></button>
            </div>

            <div className="choose-file-input d-flex align-items-center">
              <label className="btn choose-file-btn me-2" htmlFor={`mt202FileInput`}>
                Choose File
              </label>
              <input
                id={`mt202FileInput`}
                type="file"
                style={{ display: "none" }}
                accept=".txt,.csv,.xlsx,.xls,.pdf"
                onChange={handleFileChange}
              />
              <p className="mb-0 choose-file-name flex-grow-1" onClick={() => document.getElementById(`mt202FileInput`).click()}>
                {content.file ? content.file.name : "No file chosen"}
              </p>
            </div>

            <div className="d-flex justify-content-center align-items-center mt-3">
              <button type="button" className="btn createButton me-3" onClick={handleSubmit}>
                Submit
              </button>
              <button type="button" className="btn cancelButton" onClick={() => setImportModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {viewModal && (
        <div className="modal-overlay d-flex flex-column">
          <div className="modal-container">
            {selectedFileDetails ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mt-4">MT202 File Details</h5>
                  <button className="border-0 bg-transparent" onClick={() => setViewModal(false)}>
                    <MdClose fontSize={20} />
                  </button>
                </div>

                <table className="table table-bordered table-striped">
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedFileDetails.map((detail, index) => (
                      <tr key={index}>
                        <td>{(modalCurrentPage - 1) * modalPageSize + index + 1}</td>
                        <td>{detail.original_name}</td>
                        <td>{detail.scenario}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="d-flex justify-content-end mt-3">
                  <div className="pagination">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleModalFirstPage}
                      disabled={modalCurrentPage === 1}
                      className="me-2"
                    >
                      First
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleModalPrevPage}
                      disabled={modalCurrentPage === 1}
                      className="me-2"
                    >
                      Prev
                    </Button>
                    <span className="mx-2 align-self-center">
                      Page {modalCurrentPage} of {Math.ceil(modalTotalCount / modalPageSize)}
                    </span>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleModalNextPage}
                      disabled={modalCurrentPage === Math.ceil(modalTotalCount / modalPageSize)}
                      className="me-2"
                    >
                      Next
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleModalLastPage}
                      disabled={modalCurrentPage === Math.ceil(modalTotalCount / modalPageSize)}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center mt-4">
                <img src={require("../../../assets/loader.gif")} alt="loader" style={{ width: "50px" }} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default MT202Tab;
